import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Stack, Typography, Grid, Skeleton, Button } from "@mui/material";
import { dbService } from "../firebaseInstance";
import { useIsMobile } from "../hooks/useIsMobile";
import { useResponsive } from "../hooks/useResponsive";
import { useTranslation } from "react-i18next";
// import threadsWordmark from "../assets/logo/threads-logo-wordmark-black.svg";
import IssueCard from "../components/magazine/IssueCard";
import { AllCollectionHeroSection } from "../template/allCollection/AllCollectionHeroSection";
import { getAllCollection } from "../utils/pureDataFetch";
// Removed: moved into MonthlyThreadsCollectionsSection
import { subscribeCategories } from "../services/adminCategoryManager";
import { preloadAvatars } from "../utils/avatarCacheSystem";
import { PageContainer } from "../components/Container/PageContainer";
import SectionTitle from "../template/magazine/SectionTitle";
import LineGrid from "../template/magazine/LineGrid";
import MonthlyThreadsCollectionsSection from "../template/magazine/MonthlyThreadsCollectionsSection";
import ParticipantsShowcaseSection from "../template/magazine/ParticipantsShowcaseSection";
import { getPublicAvatarUrl } from "../utils/publicAvatarUtils";
import { getThreadsPeople } from "../utils/dataFetch";
import MultiCarousel from "../components/carousel/MultiCarousel";
import MagazineCollectionCard from "../template/magazine/MagazineCollectionCard";

// Helper: 해당 컬렉션의 threadList에서 가장 최신 timestamp(ms) 추출 (모듈 스코프)
function getMaxThreadTimestampMs(collection) {
	try {
		if (
			!Array.isArray(collection?.threadList) ||
			collection.threadList.length === 0
		) {
			return -Infinity;
		}
		let maxMs = -Infinity;
		for (const th of collection.threadList) {
			const v = th?.timestamp;
			let ms;
			try {
				if (v == null) {
					ms = -Infinity;
				} else if (typeof v?.toDate === "function") {
					ms = v.toDate().getTime();
				} else if (typeof v === "number") {
					ms = v >= 1e12 ? v : v * 1000;
				} else {
					const t = Date.parse(v);
					ms = isNaN(t) ? -Infinity : t;
				}
			} catch (_) {
				ms = -Infinity;
			}
			if (ms > maxMs) maxMs = ms;
		}
		return maxMs;
	} catch (_) {
		return -Infinity;
	}
}

const MAG_ROOT = "magazines";
const MAG_ID = "monthlyThreads";

const MonthlyThreads = () => {
	const navigate = useNavigate();
	const isMobile = useIsMobile("md");
	const { smDown } = useResponsive();
	const { t } = useTranslation();
	const [issues, setIssues] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	// 컬렉션/카테고리 상태
	const [collections, setCollections] = useState([]);
	const [collectionsLoading, setCollectionsLoading] = useState(true);
	const [categories, setCategories] = useState([]);
	const [selectedCategory, setSelectedCategory] = useState(null);

	// ThreadsPeople 디렉토리에서만 쇼케이스 데이터 가져오기
	const [participantsForShowcase, setParticipantsForShowcase] = useState([]);
	useEffect(() => {
		let cancelled = false;
		(async () => {
			try {
				const list = await getThreadsPeople({ limit: 24, activeOnly: true });
				if (cancelled) return;
				const people = Array.isArray(list) ? list : [];
				const mapped = people.map((p) => {
					const id = String(p.id || p.userId || "");
					const avatar = p.profileImage || getPublicAvatarUrl(id, 320);
					return {
						id,
						profile: {
							id,
							username: p.username || "",
							name: p.name || "",
							threads_profile_picture_url: avatar,
						},
						__fallbackAvatar: avatar,
						__categoryLabels: Array.isArray(p.categoryLabels) ? p.categoryLabels : [],
						__personBio: p.bio || "",
						__personBusinessLink: p.businessLink || null,
					};
				});
				setParticipantsForShowcase(mapped);
			} catch (_) {
				setParticipantsForShowcase([]);
			}
		})();
		return () => {
			cancelled = true;
		};
	}, []);

	useEffect(() => {
		const unsubscribe = dbService
			.collection(MAG_ROOT)
			.doc(MAG_ID)
			.collection("issues")
			.onSnapshot(
				(snapshot) => {
					const issuesList = snapshot.docs
						.map((doc) => ({
							id: doc.id,
							...doc.data(),
						}))
						// 모든 이슈 표시 (발행됨/미발행됨 포함)
						.sort((a, b) => {
							// 클라이언트 측 정렬: year 내림차순, month 내림차순
							if (b.year !== a.year) {
								return b.year - a.year;
							}
							return b.month - a.month;
						});
					setIssues(issuesList);
					setLoading(false);
				},
				(err) => {
					console.error("Failed to load issues:", err);
					setError(err.message);
					setLoading(false);
				}
			);

		return () => unsubscribe();
	}, []);

	// 컬렉션 불러오기
	useEffect(() => {
		const fetchCollections = async () => {
			try {
				setCollectionsLoading(true);
				const allCollections = await getAllCollection();
				setCollections(allCollections);
			} catch (e) {
				console.error("Error fetching all collections:", e);
			} finally {
				setCollectionsLoading(false);
			}
		};

		fetchCollections();
	}, []);

	// 카테고리 구독
	useEffect(() => {
		const unsub = subscribeCategories(
			(list) => setCategories(list),
			(e) => console.error("Error subscribing categories:", e)
		);
		return () => unsub && unsub();
	}, []);

	// 히스토리 기반 스크롤 위치 저장/복원
	useEffect(() => {
		const restoreScrollFromHistory = () => {
			const state = window.history.state;
			if (state?.scrollY && typeof state.scrollY === "number") {
				const attemptRestore = (delay = 0) => {
					setTimeout(() => {
						window.scrollTo(0, state.scrollY);
					}, delay);
				};
				attemptRestore(0);
				attemptRestore(100);
				attemptRestore(300);
				attemptRestore(500);
			}
		};

		// 초기 복원
		restoreScrollFromHistory();

		let scrollTimeout;
		const saveScrollToHistory = () => {
			clearTimeout(scrollTimeout);
			scrollTimeout = setTimeout(() => {
				const currentState = window.history.state || {};
				const newState = { ...currentState, scrollY: window.scrollY };
				window.history.replaceState(
					newState,
					"",
					window.location.pathname + window.location.search
				);
			}, 100);
		};

		const handlePopstate = (event) => {
			if (event.state?.scrollY) {
				setTimeout(() => {
					window.scrollTo(0, event.state.scrollY);
				}, 100);
			}
		};

		window.addEventListener("scroll", saveScrollToHistory);
		window.addEventListener("beforeunload", saveScrollToHistory);
		window.addEventListener("popstate", handlePopstate);

		return () => {
			saveScrollToHistory();
			clearTimeout(scrollTimeout);
			window.removeEventListener("scroll", saveScrollToHistory);
			window.removeEventListener("beforeunload", saveScrollToHistory);
			window.removeEventListener("popstate", handlePopstate);
		};
	}, []);

	// 아바타 프리로드 (백그라운드)
	useEffect(() => {
		if (collections.length > 0) {
			const uniqueUserIDs = [
				...new Set(
					collections
						.filter((collection) => collection.userID)
						.map((collection) => collection.userID)
				),
			];
			if (uniqueUserIDs.length > 0) {
				preloadAvatars(uniqueUserIDs);
			}
		}
	}, [collections]);

	// 다이나믹 그리드 크기 패턴 (PC 버전에서만 적용)
	const getDynamicGridSize = (index) => {
		if (isMobile) {
			return {
				xs: 6,
				sm: 6,
				md: 4,
				lg: 3,
			};
		}

		const patterns = [
			{ lg: 6, md: 6 },
			{ lg: 3, md: 3 },
			{ lg: 3, md: 3 },
			{ lg: 3, md: 3 },
			{ lg: 6, md: 6 },
			{ lg: 3, md: 3 },
			{ lg: 3, md: 3 },
			{ lg: 3, md: 3 },
			{ lg: 6, md: 6 },
		];

		const patternIndex = index % patterns.length;
		return {
			xs: 12,
			sm: 6,
			...patterns[patternIndex],
		};
	};

	// 컬렉션 필터링/정렬/셔플
	const filteredCollections = useMemo(() => {
		let filtered = collections
			.filter((collection) => {
				const isPublic = collection.isPublic ?? true;
				return isPublic && collection.title && collection.title.length > 0;
			})
			// lastUpdated(2025 이전이면 threadList 최신으로 대체) 우선, createdAt 보조 정렬 (내림차순)
			.sort((a, b) => {
				const thresholdMs = Date.parse("2025-01-01T00:00:00Z");
				const aLastRaw = normalizeToMs(a?.lastUpdated);
				const bLastRaw = normalizeToMs(b?.lastUpdated);
				const aFallback =
					aLastRaw < thresholdMs ? getMaxThreadTimestampMs(a) : -Infinity;
				const bFallback =
					bLastRaw < thresholdMs ? getMaxThreadTimestampMs(b) : -Infinity;
				const aLast = aFallback !== -Infinity ? aFallback : aLastRaw;
				const bLast = bFallback !== -Infinity ? bFallback : bLastRaw;
				if (bLast !== aLast) return bLast - aLast;
				const aCreated = normalizeToMs(a.createdAt);
				const bCreated = normalizeToMs(b.createdAt);
				return bCreated - aCreated;
			});

		if (selectedCategory) {
			filtered = filtered.filter((collection) => {
				const collectionCategories = collection.categoryKeys || [];
				return collectionCategories.includes(selectedCategory);
			});
		}

		// 셔플 제거: 정렬 결과 그대로 반환
		return filtered;
	}, [collections, selectedCategory]);

	const categoryLabelMap = useMemo(() => {
		const map = new Map();
		categories.forEach((c) => {
			const label = (c.labels && (c.labels.ko || c.labels.en)) || c.key;
			map.set(c.key, label);
		});
		return map;
	}, [categories]);

	const sortedCategories = useMemo(() => {
		return categories
			.filter((cat) => cat.isActive !== false)
			.map((cat) => {
				const count = collections.filter((collection) => {
					const isPublic = collection.isPublic ?? true;
					if (!isPublic || !collection.title || collection.title.length === 0)
						return false;
					const collectionCategories = collection.categoryKeys || [];
					return collectionCategories.includes(cat.key);
				}).length;
				return { ...cat, count };
			})
			.sort((a, b) => b.count - a.count);
	}, [categories, collections]);

	// 최근 컬렉션 10개 (전체 기준, 공개/제목 필터 후 최신순)
	const recentCollections = useMemo(() => {
		try {
			const filtered = collections
				.filter((collection) => {
					const isPublic = collection.isPublic ?? true;
					return isPublic && collection.title && collection.title.length > 0;
				})
				.sort((a, b) => {
					const thresholdMs = Date.parse("2025-01-01T00:00:00Z");
					const aLastRaw = normalizeToMs(a?.lastUpdated);
					const bLastRaw = normalizeToMs(b?.lastUpdated);
					const aFallback =
						aLastRaw < thresholdMs ? getMaxThreadTimestampMs(a) : -Infinity;
					const bFallback =
						bLastRaw < thresholdMs ? getMaxThreadTimestampMs(b) : -Infinity;
					const aLast = aFallback !== -Infinity ? aFallback : aLastRaw;
					const bLast = bFallback !== -Infinity ? bFallback : bLastRaw;
					if (bLast !== aLast) return bLast - aLast;
					const aCreated = normalizeToMs(a.createdAt);
					const bCreated = normalizeToMs(b.createdAt);
					return bCreated - aCreated;
				});

			return filtered.slice(0, 10);
		} catch (_) {
			return [];
		}
	}, [collections]);

	const getDisplayCategoryLabels = (collection) => {
		if (!collection) return [];
		if (
			Array.isArray(collection.categoryLabels) &&
			collection.categoryLabels.length > 0
		) {
			return collection.categoryLabels;
		}
		if (
			Array.isArray(collection.categoryKeys) &&
			collection.categoryKeys.length > 0
		) {
			return collection.categoryKeys.map((k) => categoryLabelMap.get(k) || k);
		}
		return [];
	};

	const handleCategoryClick = (categoryKey) => {
		if (categoryKey === "all") {
			setSelectedCategory(null);
		} else {
			setSelectedCategory((prev) =>
				prev === categoryKey ? null : categoryKey
			);
		}
	};

	// 날짜 정규화(Temporary debug helper) - 필요 시만 사용
	// 제거: 현재 미사용으로 경고 방지

	// 정렬용 ms 단위 정규화 (Timestamp/초/밀리초/ISO 모두 지원)
	function normalizeToMs(v) {
		if (v == null) return -Infinity;
		try {
			if (typeof v.toDate === "function") return v.toDate().getTime();
			if (typeof v === "number") {
				return v >= 1e12 ? v : v * 1000;
			}
			const t = Date.parse(v);
			return isNaN(t) ? -Infinity : t;
		} catch (_) {
			return -Infinity;
		}
	}

	// (정렬에 인라인 사용으로 별도 함수 제거)

	// 비디오 URL 생성 헬퍼
	const getVideoUrl = (issueId) => {
		if (!issueId) return null;
		return `https://firebasestorage.googleapis.com/v0/b/my-threads-insight.firebasestorage.app/o/videos%2Fmonthly-cover-${issueId}.mp4?alt=media`;
	};

	// 이슈 날짜 포맷팅
	const formatIssueDate = (issueId) => {
		const match = issueId?.match(/(\d{4})-(\d{2})/);
		if (!match) return issueId;
		const [, year, month] = match;
		return t("magazine.issueFormat", { year, month: parseInt(month) });
	};

	const handleIssueClick = (issue) => {
		// 발행된 이슈만 클릭 가능
		if (issue.isPublished) {
			navigate(`/monthly-threads/${issue.id}`);
		}
		// 미발행 이슈는 클릭해도 아무것도 하지 않음
	};

	if (loading) {
		return (
			<Box sx={{ width: "100%", p: isMobile ? 2 : 4 }}>
				<Stack spacing={4}>
					<Skeleton variant="text" sx={{ fontSize: "2rem", width: "40%" }} />
					<Grid container spacing={isMobile ? 2 : 4}>
						{[...Array(6)].map((_, index) => (
							<Grid size={{ xs: 12, sm: 6, md: 4 }} key={index}>
								<Skeleton
									variant="rectangular"
									height={400}
									sx={{ borderRadius: 2 }}
								/>
							</Grid>
						))}
					</Grid>
				</Stack>
			</Box>
		);
	}

	if (error) {
		return (
			<Box sx={{ width: "100%", p: isMobile ? 2 : 4 }}>
				<Typography color="error">
					{t("error.loadFailed")}: {error}
				</Typography>
			</Box>
		);
	}

	// 빈 이슈일 때도 플레이스홀더를 렌더링하기 위해 early-return 제거

	return (
		<>
			<AllCollectionHeroSection
				titleKey="magazine.monthlyThreadsTitle"
				descriptionKey="allCollection.description"
				collections={collections}
			/>
			<PageContainer
				expand={true}
				sx={{
					marginTop: isMobile ? "calc(-30vh - 80px)" : "calc(-30vh - 96px)",
					// backgroundColor: "background.default",
					position: "relative",
					zIndex: (theme) => theme.zIndex.appBar - 1,
				}}
			>
				<Stack spacing={isMobile ? 8 : 16} pt={isMobile ? 6 : 12}>
					{/* Issues Grid */}
					<Stack spacing={0}>
						<SectionTitle title={t("magazine.monthlyThreadsTitle")} />
						<Box
							sx={{
								width: "100%",
								overflow: "hidden",
								borderBottom: "1px solid",
								borderColor: "primary.main",
							}}
						>
							<LineGrid
								gap={smDown ? 24 : 64}
								borderColor="primary.main"
								gridColumns={smDown ? 2 : 3}
							>
								{issues.slice(0, 6).map((issue, idx, arr) => {
									const isFirstFullMobile =
										smDown && arr.length % 2 === 1 && idx === 0;
									const itemSize = smDown
										? { xs: isFirstFullMobile ? 12 : 6 }
										: { sm: 4, md: 4, lg: 4 };
									return (
										<Grid key={issue.id} size={itemSize}>
											<IssueCard
												issue={issue}
												onIssueClick={handleIssueClick}
												getVideoUrl={getVideoUrl}
												formatIssueDate={formatIssueDate}
												isMobile={isMobile}
											/>
										</Grid>
									);
								})}
							</LineGrid>
						</Box>
					</Stack>

					{/* Participants Showcase (between hero and collections) */}
					{participantsForShowcase.length > 0 && (
						<ParticipantsShowcaseSection
							title={"스레드 인물 사전"}
							participants={participantsForShowcase}
							isMobile={isMobile}
							action={(
								<Button
									variant="contained"
									size={isMobile ? "large" : "large"}
									onClick={() => navigate('/people')}
									sx={{
										textTransform: 'none',
										fontWeight: 800,
										px: isMobile ? 3 : 5,
										py: isMobile ? 1.25 : 1.5,
										borderRadius: 999,
									}}
								>
									전체 보기
								</Button>
							)}
						/>
					)}

					{/* Recent Collections Carousel */}
					{Array.isArray(recentCollections) && recentCollections.length > 0 && (
						<Stack spacing={0}>
							<SectionTitle title={t('allCollection.recentTitle')} />
							<Box sx={{
								width: '100%',
								maxWidth: !isMobile ? '100vw' : 'calc(100vw - 16px)',
								mb: 3,
								position: 'relative',
								px: 0,
								minWidth: 0,
								contain: 'layout style',
								boxSizing: 'border-box',
								borderBottom: '1px solid',
								borderColor: 'primary.main',
							}}>
								<MultiCarousel
									items={recentCollections}
									visible={{ xs: 2, sm: 2, md: 3, lg: 4 }}
									gap={0}
									step={1}
									showDivider={true}
									dividerColor="primary.main"
									renderItem={(collection) => {
										const userId = collection.userID || collection.userId || collection.user_id;
										const user = {
											id: String(userId || ''),
											profile: {
												id: String(userId || ''),
												username: collection.username || '',
												name: collection.name || collection.username || '',
											},
										};
										return (
											<Box sx={{ width: '100%' }}>
												<MagazineCollectionCard
													collection={collection}
													user={user}
													isMobile={isMobile}
													size={'default'}
													categoryLabels={getDisplayCategoryLabels(collection)}
												/>
											</Box>
										);
									}}
								/>
							</Box>
						</Stack>
					)}

					{/* Collections Section */}
					<MonthlyThreadsCollectionsSection
						isMobile={isMobile}
						collections={collections}
						collectionsLoading={collectionsLoading}
						sortedCategories={sortedCategories}
						selectedCategory={selectedCategory}
						onCategoryClick={handleCategoryClick}
						filteredCollections={filteredCollections}
						getDisplayCategoryLabels={getDisplayCategoryLabels}
						getDynamicGridSize={getDynamicGridSize}
					/>
				</Stack>
			</PageContainer>
		</>
	);
};

export default MonthlyThreads;
